import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import Button, {
  BUTTON_VARIANT_DANGER,
  BUTTON_VARIANT_FILLED,
  BUTTON_VARIANT_TEXT,
  type ButtonVariant,
} from '../../atoms/Button';
import Modal from '../Modal';
import {
  CONFIRM_DIALOG_TONE_DANGER,
  type ConfirmDialogTone,
} from './ConfirmDialog.constants';
import {
  Description,
  FooterRow,
  HeaderIconDisc,
} from './ConfirmDialog.styled';

export interface ConfirmDialogProps {
  /** Controls visibility. */
  open: boolean;
  /**
   * Called when the user dismisses without confirming — backdrop click,
   * Escape, the explicit cancel button, or the modal close button.
   */
  onClose: () => void;
  /**
   * Called when the user clicks the confirm button. May return a
   * `Promise` — while it's pending the confirm button shows a spinner
   * and the dialog ignores further dismiss attempts. The dialog calls
   * `onClose` automatically once the promise resolves successfully;
   * rejected promises leave the dialog open so the consumer can surface
   * the error inline (e.g. by setting `description` to the error
   * message).
   */
  onConfirm: () => void | Promise<void>;
  /** Title shown in the modal header. */
  title: ReactNode;
  /**
   * Body text — typically a single sentence explaining the consequence
   * of confirming. Pass any node when richer formatting is needed.
   */
  description?: ReactNode;
  /** Tone of the confirm action. Defaults to `danger`. */
  tone?: ConfirmDialogTone;
  /** Confirm-button label. Defaults vary by tone (`Delete` / `Continue` / `OK`). */
  confirmLabel?: string;
  /** Cancel-button label. Defaults to `Cancel`. */
  cancelLabel?: string;
  /**
   * Override the auto-injected tinted header icon (e.g. an `MdDelete` for
   * danger). Pass `null` to opt out of the header icon entirely.
   */
  icon?: ReactNode | null;
  /**
   * Externally control the loading state of the confirm button. Useful
   * when the consumer is doing the async work outside this component
   * (e.g. via a mutation hook). Defaults to whatever the internal
   * `onConfirm` promise tracking decides.
   */
  loading?: boolean;
  /**
   * Disable the confirm button (e.g. while a "type the name to confirm"
   * field is empty). Cancel + dismiss remain available.
   */
  confirmDisabled?: boolean;
  /** Match the modal's underlying portal container override. */
  container?: HTMLElement | null;
  className?: string;
  dataTestId?: string;
}

const DefaultDangerGlyph = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M3 19h18L12 4 3 19Zm9-3h-1v-1h1v1Zm0-3h-1v-4h1v4Z"
      fill="currentColor"
    />
  </svg>
);

const DefaultCautionGlyph = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2Zm-1 14h2v2h-2Zm0-9h2v7h-2Z"
      fill="currentColor"
    />
  </svg>
);

const DefaultInfoGlyph = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2Zm0 5a1.5 1.5 0 1 1-1.5 1.5A1.5 1.5 0 0 1 12 7Zm1 11h-2v-7h2Z"
      fill="currentColor"
    />
  </svg>
);

const DEFAULT_GLYPH: Record<ConfirmDialogTone, React.FC> = {
  danger: DefaultDangerGlyph,
  caution: DefaultCautionGlyph,
  info: DefaultInfoGlyph,
};

const DEFAULT_CONFIRM_LABEL: Record<ConfirmDialogTone, string> = {
  danger: 'Delete',
  caution: 'Continue',
  info: 'OK',
};

const CONFIRM_VARIANT: Record<ConfirmDialogTone, ButtonVariant> = {
  danger: BUTTON_VARIANT_DANGER,
  caution: BUTTON_VARIANT_FILLED,
  info: BUTTON_VARIANT_FILLED,
};

/**
 * Destructive-action confirmation modal — composes {@link Modal} with a
 * tone-tinted header icon and a footer of cancel + confirm buttons.
 *
 * Two ways to drive the loading state:
 *   1. **Internal** (default): return a `Promise` from `onConfirm` and
 *      the dialog owns the spinner + auto-closes on resolve.
 *   2. **External**: pass an explicit `loading` prop when the consumer
 *      is doing the async work in a parent (e.g. a mutation hook with
 *      its own `isPending` state).
 *
 * @example Internal loading
 *   <ConfirmDialog
 *     open={open}
 *     onClose={() => setOpen(false)}
 *     onConfirm={async () => { await deleteProcess(id); }}
 *     title="Delete process?"
 *     description={`This permanently deletes "${name}".`}
 *   />
 */
const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  description,
  tone = CONFIRM_DIALOG_TONE_DANGER,
  confirmLabel,
  cancelLabel = 'Cancel',
  icon,
  loading: externalLoading,
  confirmDisabled = false,
  container,
  className,
  dataTestId = 'confirm-dialog',
}) => {
  const [internalLoading, setInternalLoading] = useState(false);
  const isControlledLoading = externalLoading !== undefined;
  const loading = isControlledLoading ? !!externalLoading : internalLoading;

  // Session token bumped on every open transition AND on unmount. The
  // pending confirm captures the current session at click time and
  // the .then handlers bail if the token has moved on, so a slow
  // promise from a previous session can never:
  //   (a) clear loading on a new session,
  //   (b) call onClose on a freshly-opened dialog,
  //   (c) call setState after unmount.
  // Critical when the parent forcibly closes mid-confirm (e.g. nav
  // event) and reopens the dialog with new content.
  const sessionRef = useRef(0);
  useEffect(() => {
    if (open) {
      sessionRef.current += 1;
      // Reset internal loading on each fresh open so a leftover from a
      // previous (now-invalidated) session can't bleed through.
      setInternalLoading(false);
    }
  }, [open]);
  // Invalidate any in-flight session on unmount as well, so a
  // late-resolving promise can't try to setState on a dead component.
  useEffect(
    () => () => {
      sessionRef.current += 1;
    },
    [],
  );

  const resolvedConfirmLabel = confirmLabel ?? DEFAULT_CONFIRM_LABEL[tone];
  const Glyph = DEFAULT_GLYPH[tone];
  // `icon === null` is the explicit opt-out; `undefined` falls back to
  // the tone-default glyph.
  const headerIcon =
    icon === null ? null : (
      <HeaderIconDisc
        $tone={tone}
        data-testid={`${dataTestId}__header-icon`}
        data-tone={tone}
      >
        {icon ?? <Glyph />}
      </HeaderIconDisc>
    );

  const handleConfirm = useCallback(() => {
    if (loading || confirmDisabled) return;
    // Sync throws propagate to React's onClick handler / error boundary;
    // we deliberately don't catch them so the consumer can recover.
    const result = onConfirm();

    if (isControlledLoading) {
      // Externally controlled — consumer owns both loading + closing.
      return;
    }

    const isPromise =
      !!result && typeof (result as Promise<void>).then === 'function';

    if (!isPromise) {
      // Sync confirm — auto-close so trivial callers don't need to wire
      // both `onConfirm` and `onClose`.
      onClose();
      return;
    }

    // Capture the current session so a slow / rejected promise from
    // *this* open cannot mutate state for a *later* open (the parent
    // may forcibly close + reopen the dialog while we're awaiting).
    const session = sessionRef.current;
    // Async confirm — drive the spinner via .then(ok, fail) instead of
    // `await` so a rejection lives in promise-context (consumer can
    // observe via `unhandledrejection` listener / Sentry) instead of
    // bubbling out of an event handler as an unhandled error.
    setInternalLoading(true);
    (result as Promise<void>).then(
      () => {
        if (sessionRef.current !== session) return;
        setInternalLoading(false);
        onClose();
      },
      () => {
        // Rejected — keep the dialog open so the consumer can surface
        // the failure (toast, inline error). Drop the loading state.
        if (sessionRef.current !== session) return;
        setInternalLoading(false);
      },
    );
  }, [
    loading,
    confirmDisabled,
    onConfirm,
    onClose,
    isControlledLoading,
  ]);

  // While confirming, both Esc and backdrop dismiss must be ignored —
  // closing mid-mutation would leave the user in an undefined state.
  const isBlocking = loading;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      icon={headerIcon}
      bareIcon
      size="sm"
      hideCloseButton={isBlocking}
      disableEscape={isBlocking}
      disableBackdropClose={isBlocking}
      container={container}
      className={className}
      dataTestId={dataTestId}
      footer={
        <FooterRow data-testid={`${dataTestId}__footer-row`}>
          <Button
            variant={BUTTON_VARIANT_TEXT}
            onClick={onClose}
            disabled={isBlocking}
            dataTestId={`${dataTestId}__cancel`}
          >
            {cancelLabel}
          </Button>
          <Button
            variant={CONFIRM_VARIANT[tone]}
            onClick={handleConfirm}
            loading={loading}
            disabled={confirmDisabled}
            dataTestId={`${dataTestId}__confirm`}
          >
            {resolvedConfirmLabel}
          </Button>
        </FooterRow>
      }
    >
      {description != null && (
        <Description data-testid={`${dataTestId}__description`}>
          {description}
        </Description>
      )}
    </Modal>
  );
};

export default ConfirmDialog;
